import { useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Area, CartesianGrid, ComposedChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { AnalyticsEngagementPoint } from '@proref/types';

// The 30-day engagement chart: two filled area lines — active officials (ink) vs clips completed
// (accent) — over a shared, zero-based scale. Built on Recharts (the app's chart library, §2) rather
// than the hand-rolled SVG this replaces; the backend already zero-fills every day in the window (see
// analytics_engagement), so — unlike RefereeTrendChart's accuracy line — there's no null-gap to guard.

interface EngagementChartProps {
  data: AnalyticsEngagementPoint[];
}

// Five week markers along the x-axis (W-4 … Today), same positions as the design this replaces.
const TICK_INDICES = [0, 7, 14, 21, 29];
const TICK_LABEL_KEYS = ['tickW4', 'tickW3', 'tickW2', 'tickW1', 'tickToday'] as const;

interface TooltipEntry {
  payload?: AnalyticsEngagementPoint;
}
interface EngagementTooltipProps {
  active?: boolean;
  payload?: TooltipEntry[];
}

function EngagementTooltip({ active, payload }: EngagementTooltipProps) {
  const { t } = useTranslation();
  const row = payload?.[0]?.payload;
  if (!active || !row) return null;
  return (
    <div className="rounded border border-line bg-surface px-3 py-2 text-[11.5px] shadow-1">
      <div className="mb-1 font-medium text-ink">{row.date}</div>
      <div className="flex items-center justify-between gap-4">
        <span className="inline-flex items-center gap-1.5 text-muted">
          <span className="h-2 w-2 rounded-full" style={{ background: 'var(--ink)' }} />
          {t('admin.analytics.dashboard.legendActive')}
        </span>
        <span className="tabular-nums text-ink">{row.active}</span>
      </div>
      <div className="flex items-center justify-between gap-4">
        <span className="inline-flex items-center gap-1.5 text-muted">
          <span className="h-2 w-2 rounded-full" style={{ background: 'var(--accent)' }} />
          {t('admin.analytics.dashboard.legendCompletions')}
        </span>
        <span className="tabular-nums text-ink">{row.completions}</span>
      </div>
    </div>
  );
}

export function EngagementChart({ data }: EngagementChartProps) {
  const { t } = useTranslation();
  // Unique gradient ids per instance so two charts on a page never collide.
  const uid = useId();
  const activeGrad = `eng-active-${uid}`;
  const compGrad = `eng-comp-${uid}`;

  // Maps each tick's real date value (what XAxis needs to match against) to its translated label —
  // built from data rather than assumed positions, so a shorter series just drops the ticks past it.
  const tickLabelByDate = useMemo(() => {
    const map = new Map<string, string>();
    TICK_INDICES.forEach((i, idx) => {
      const point = data[i];
      if (point) map.set(point.date, t(`admin.analytics.engagement.${TICK_LABEL_KEYS[idx]}`));
    });
    return map;
  }, [data, t]);

  return (
    <div role="img" aria-label={t('admin.analytics.engagement.ariaLabel')}>
      <ResponsiveContainer width="100%" height={200}>
        <ComposedChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: -4 }}>
          <CartesianGrid stroke="var(--line)" strokeDasharray="2 4" vertical={false} />
          <defs>
            <linearGradient id={activeGrad} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--ink)" stopOpacity="0.16" />
              <stop offset="100%" stopColor="var(--ink)" stopOpacity="0" />
            </linearGradient>
            <linearGradient id={compGrad} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.2" />
              <stop offset="100%" stopColor="var(--accent)" stopOpacity="0" />
            </linearGradient>
          </defs>
          <XAxis
            dataKey="date"
            ticks={[...tickLabelByDate.keys()]}
            tickFormatter={(d: string) => tickLabelByDate.get(d) ?? ''}
            tick={{ fontSize: 9, fill: 'var(--muted)' }}
            tickLine={false}
            axisLine={{ stroke: 'var(--line)' }}
            interval={0}
          />
          <YAxis hide domain={[0, 'dataMax']} />
          <Tooltip content={<EngagementTooltip />} cursor={{ stroke: 'var(--line)', strokeDasharray: '2 4' }} />
          {/* isAnimationActive={false}: Recharts grows marks from the baseline on a
              requestAnimationFrame loop, so anything starving rAF freezes them at their ~0px first
              frame and the chart reads as EMPTY rather than as wrong (same reasoning as CostTrendChart
              / RefereeTrendChart). */}
          <Area
            type="monotone"
            dataKey="active"
            stroke="var(--ink)"
            strokeWidth={1.6}
            fill={`url(#${activeGrad})`}
            dot={false}
            isAnimationActive={false}
          />
          <Area
            type="monotone"
            dataKey="completions"
            stroke="var(--accent)"
            strokeWidth={1.6}
            fill={`url(#${compGrad})`}
            dot={false}
            isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
